import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  MessageCircle, X, Search, Compass, BookOpen, Send, Loader2, Sparkle,
  Minus, Trash2, CornerDownLeft,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HISTORY_KEY = 'emina_assistant_history';
const STATE_KEY = 'emina_assistant_state'; // 'open' | 'minimized' | 'hidden'
const HIDDEN_KEY = 'emina_assistant_hidden';
const MAX_HISTORY = 50;
const LLM_CONTEXT_TURNS = 10;

const NAV_DESTINATIONS = [
  { keywords: ['poc', 'pocetna', 'početna', 'home'], label: 'Početna', path: '/' },
  { keywords: ['kontakt', 'contact'], label: 'Kontakt', path: '/kontakt' },
  { keywords: ['oglasavanje', 'oglašavanje', 'ad', 'oglasi'], label: 'Oglašavanje', path: '/oglasavanje' },
  { keywords: ['pretplata', 'pretplatu', 'subscribe', 'subscription'], label: 'Pretplata', path: '/pretplata' },
  { keywords: ['posao', 'jobs', 'radno mjesto'], label: 'Posao', path: '/posao' },
  { keywords: ['oglasnik', 'classified'], label: 'Oglasnik', path: '/oglasnik' },
  { keywords: ['predaj', 'submit', 'vijest predaj'], label: 'Predaj vijest', path: '/predaj-vijest' },
  { keywords: ['o nama', 'about'], label: 'O nama', path: '/o-nama' },
  { keywords: ['impressum'], label: 'Impressum', path: '/impressum' },
  { keywords: ['privatnost', 'privacy'], label: 'Politika privatnosti', path: '/politika-privatnosti' },
  { keywords: ['kolacica', 'kolačić', 'cookie'], label: 'Postavke kolačića', path: '/postavke-kolacica' },
];

const CATEGORY_KEYS = [
  { keys: ['hrvatska', 'hrvatske', 'hrvatsku'], slug: 'hrvatska-vijesti', label: 'Hrvatska vijesti' },
  { keys: ['varazdin', 'varaždin', 'varazdinske', 'varaždinske'], slug: 'varazdinske-vijesti', label: 'Varaždinske vijesti' },
  { keys: ['crna kronika', 'kronika', 'incident'], slug: 'crna-kronika', label: 'Crna kronika' },
  { keys: ['kultura'], slug: 'kultura', label: 'Kultura' },
  { keys: ['turizam'], slug: 'turizam-u-medimurju', label: 'Turizam' },
  { keys: ['dogadjanja', 'događanja', 'događaji', 'events'], slug: 'lokalna-dogadanja-u-medimurju', label: 'Događanja' },
  { keys: ['sport'], slug: 'sport', label: 'Sport' },
  { keys: ['gastronom', 'hrana', 'restoran'], slug: 'gastronomija', label: 'Gastronomija' },
  { keys: ['obrazovanje', 'škola', 'skola'], slug: 'obrazovanje', label: 'Obrazovanje' },
  { keys: ['zdravstvo', 'zdravlj'], slug: 'zdravstvo', label: 'Zdravstvo' },
];

const NAV_VERBS = ['otvori', 'idi', 'odvedi', 'prebaci', 'navigiraj', 'go to', 'open', 'take me'];
const SUMMARY_VERBS = ['sažmi', 'sazmi', 'objasni', 'ukratko', 'summary', 'summarize', 'summarise', 'rezimiraj'];

function classify(text, ctx) {
  const t = (text || '').toLowerCase().trim();
  if (!t) return { intent: 'unknown' };
  if (SUMMARY_VERBS.some(v => t.includes(v))) {
    if (ctx.articleId) return { intent: 'article_summary' };
    return { intent: 'article_summary_no_context' };
  }
  if (NAV_VERBS.some(v => t.startsWith(v)) || t.startsWith('pokaži ')) {
    const dest = NAV_DESTINATIONS.find(d => d.keywords.some(k => t.includes(k)));
    if (dest) return { intent: 'navigation', target: dest };
    const cat = CATEGORY_KEYS.find(c => c.keys.some(k => t.includes(k)));
    if (cat) return { intent: 'navigation', target: { label: cat.label, path: `/kategorija/${cat.slug}` } };
  }
  const catMatch = CATEGORY_KEYS.find(c => c.keys.some(k => t === k));
  if (catMatch) return { intent: 'navigation', target: { label: catMatch.label, path: `/kategorija/${catMatch.slug}` } };
  return { intent: 'search', q: t };
}

function loadHistory() {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.slice(-MAX_HISTORY) : [];
  } catch { return []; }
}
function saveHistory(msgs) {
  try { localStorage.setItem(HISTORY_KEY, JSON.stringify(msgs.slice(-MAX_HISTORY))); } catch {}
}

export default function EminaAsistent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();

  const [messages, setMessages] = useState(() => loadHistory());
  const [state, setState] = useState(() => localStorage.getItem(STATE_KEY) || 'hidden_closed_initial');
  const [hidden, setHidden] = useState(() => localStorage.getItem(HIDDEN_KEY) === 'true');
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState({ articles: [], categories: [] });
  const [unread, setUnread] = useState(false);

  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const scrollRef = useRef(null);
  const bubbleRef = useRef(null);

  // Derived
  const isOpen = state === 'open';
  const isMinimized = state === 'minimized';

  const articleId = (() => {
    const m = location.pathname.match(/^\/clanak\/([^/]+)/);
    return m ? m[1] : null; // slug, resolved to id later
  })();

  // Persist state
  useEffect(() => { localStorage.setItem(STATE_KEY, state); }, [state]);
  useEffect(() => { localStorage.setItem(HIDDEN_KEY, hidden ? 'true' : 'false'); }, [hidden]);
  useEffect(() => { saveHistory(messages); }, [messages]);

  // Hydrate from server when logged in
  useEffect(() => {
    if (!isAuthenticated) return;
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const r = await fetch(`${API}/assistant/history`, { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok) return;
        const d = await r.json();
        if (Array.isArray(d.history) && d.history.length) {
          // Merge: prefer server if local is empty; otherwise union-by-ts
          setMessages(prev => {
            if (prev.length === 0) return d.history.slice(-MAX_HISTORY);
            const map = new Map();
            [...prev, ...d.history].forEach(m => map.set(`${m.role}:${m.ts}:${m.content?.slice(0,40)}`, m));
            return Array.from(map.values()).slice(-MAX_HISTORY);
          });
        }
      } catch {}
    })();
  }, [isAuthenticated, getAccessTokenSilently]);

  // Focus on open
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 60);
      setUnread(false);
    }
  }, [isOpen]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  // Focus trap + ESC
  useEffect(() => {
    if (!isOpen) return;
    function onKey(e) {
      if (e.key === 'Escape') setState('minimized');
      if (e.key === 'Tab') {
        const focusables = panelRef.current?.querySelectorAll('button, input, textarea, [tabindex]:not([tabindex="-1"])');
        if (!focusables?.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen]);

  // Live search suggestions
  useEffect(() => {
    const q = input.trim();
    if (q.length < 2) { setSuggestions({ articles: [], categories: [] }); return; }
    const h = setTimeout(() => {
      fetch(`${API}/assistant/search-suggestions?q=${encodeURIComponent(q)}`)
        .then(r => r.json()).then(d => setSuggestions(d)).catch(() => {});
    }, 220);
    return () => clearTimeout(h);
  }, [input]);

  // First-time: start hidden_closed_initial → hidden or open?
  // Rule: if no state stored, show bubble (not open) — so treat unset as 'closed'.
  useEffect(() => {
    if (state === 'hidden_closed_initial') setState('closed');
  }, [state]);

  const appendMsg = useCallback((msg) => {
    setMessages(prev => [...prev, { ...msg, ts: msg.ts || new Date().toISOString() }].slice(-MAX_HISTORY));
    if (!isOpen && msg.role === 'assistant') setUnread(true);
  }, [isOpen]);

  async function resolveSlugToId(slug) {
    try {
      const r = await fetch(`${API}/public/articles/${slug}?lang=hr`);
      if (!r.ok) return null;
      const d = await r.json();
      return d.article?.id || null;
    } catch { return null; }
  }

  async function logQuery(body) {
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (isAuthenticated) {
        try { const t = await getAccessTokenSilently(); headers.Authorization = `Bearer ${t}`; } catch {}
      }
      const r = await fetch(`${API}/assistant/query`, { method: 'POST', headers, body: JSON.stringify(body) });
      return r.ok ? await r.json() : null;
    } catch { return null; }
  }

  async function send() {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    setSuggestions({ articles: [], categories: [] });
    const now = new Date().toISOString();
    const userMsg = { role: 'user', content: text, ts: now };
    appendMsg(userMsg);

    const res = classify(text, { articleId });
    const recentHistory = messages.slice(-LLM_CONTEXT_TURNS).concat([userMsg]);

    if (res.intent === 'navigation' && res.target) {
      appendMsg({ role: 'assistant', content: `Otvaram: ${res.target.label}` });
      logQuery({ query: text, intent: 'navigation', action: res.target.path, lang: 'hr', history: recentHistory });
      setTimeout(() => { navigate(res.target.path); setState('minimized'); }, 350);
      return;
    }

    if (res.intent === 'article_summary_no_context') {
      appendMsg({ role: 'assistant', content: 'Sažetak mogu napraviti samo na stranici članka. Otvorite neki članak pa probajte ponovo.' });
      logQuery({ query: text, intent: 'article_summary_no_context', lang: 'hr', history: recentHistory });
      return;
    }

    if (res.intent === 'article_summary' && articleId) {
      setLoading(true);
      const realId = await resolveSlugToId(articleId);
      if (!realId) {
        setLoading(false);
        appendMsg({ role: 'assistant', content: 'Ne mogu pronaći članak u kontekstu.' });
        return;
      }
      try {
        const d = await logQuery({
          query: text, intent: 'article_summary', article_id: realId, lang: 'hr',
          history: recentHistory,
        });
        appendMsg({ role: 'assistant', content: d?.response || 'Sažetak trenutno nije dostupan.' });
      } catch {
        appendMsg({ role: 'assistant', content: 'Greška pri dohvatu sažetka.' });
      } finally { setLoading(false); }
      return;
    }

    // Default → search
    appendMsg({ role: 'assistant', content: `Pretražujem "${text}"…` });
    logQuery({ query: text, intent: 'search', action: `/pretraga?q=${text}`, lang: 'hr', history: recentHistory });
    setTimeout(() => { navigate(`/pretraga?q=${encodeURIComponent(text)}`); setState('minimized'); }, 350);
  }

  function quickAction(kind) {
    if (kind === 'summary' && articleId) { setInput('Sažmi ovaj članak'); setTimeout(send, 50); return; }
    if (kind === 'nav') { setInput('otvori '); inputRef.current?.focus(); return; }
    if (kind === 'search') { setInput(''); inputRef.current?.focus(); return; }
  }

  function clearHistory() {
    if (!window.confirm('Obrisati cijeli razgovor?')) return;
    setMessages([]);
    localStorage.removeItem(HISTORY_KEY);
  }

  // Completely hidden — show tiny re-open dot
  if (hidden) {
    return (
      <button
        onClick={() => { setHidden(false); setState('closed'); }}
        aria-label="Otvori E-mina Asistent"
        className="fixed bottom-4 right-4 z-[55] w-9 h-9 rounded-full bg-red-600/80 hover:bg-red-600 text-white shadow-md flex items-center justify-center"
        data-testid="asistent-reopen"
      >
        <MessageCircle size={16} />
      </button>
    );
  }

  return (
    <>
      {/* Bubble — only visible when closed/minimized */}
      {!isOpen && (
        <button
          ref={bubbleRef}
          onClick={() => setState('open')}
          aria-label="E-mina Asistent"
          className={`fixed bottom-5 right-5 z-[55] ${isMinimized ? 'px-4 h-11 rounded-full gap-2' : 'w-12 h-12 rounded-full'} bg-red-600 hover:bg-red-700 text-white shadow-lg shadow-red-600/30 flex items-center justify-center transition-all hover:scale-105 active:scale-95`}
          data-testid="asistent-bubble"
        >
          <MessageCircle size={isMinimized ? 16 : 20} />
          {isMinimized && <span className="text-xs font-semibold">E-mina Asistent</span>}
          {unread && <span className="absolute top-0 right-0 w-3 h-3 rounded-full bg-white border-2 border-red-600" data-testid="asistent-unread-dot" />}
        </button>
      )}

      {/* Panel */}
      {isOpen && (
        <div
          ref={panelRef}
          className="fixed bottom-5 right-5 z-[55] w-[min(92vw,370px)] h-[min(72vh,520px)] bg-white dark:bg-[#0f172a] border border-gray-200 dark:border-gray-800 rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          data-testid="asistent-panel"
          role="dialog"
          aria-label="E-mina Asistent"
          aria-modal="false"
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800 flex items-center gap-2 bg-gradient-to-r from-red-50 to-white dark:from-[#111827] dark:to-[#0f172a]">
            <span className="w-8 h-8 rounded-full bg-white dark:bg-slate-900 ring-2 ring-red-600 shadow-md flex items-center justify-center shrink-0 overflow-hidden" data-testid="asistent-avatar">
              <img src="https://e-mina.eu/emina-mine-logo.png" alt="e-mina" className="w-7 h-7 object-contain" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold text-gray-900 dark:text-gray-100 leading-tight">E-mina Asistent</p>
              <p className="text-[10px] text-gray-500 dark:text-gray-400 leading-tight">Pretraga · Navigacija · Sažetak članka</p>
            </div>
            {messages.length > 0 && (
              <button onClick={clearHistory} aria-label="Obriši razgovor" title="Obriši razgovor" className="p-1.5 text-gray-400 hover:text-red-600" data-testid="asistent-clear">
                <Trash2 size={14} />
              </button>
            )}
            <button onClick={() => setState('minimized')} aria-label="Minimiziraj" title="Minimiziraj" className="p-1.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200" data-testid="asistent-minimize">
              <Minus size={16} />
            </button>
            <button onClick={() => { setState('closed'); setHidden(true); }} aria-label="Zatvori" title="Zatvori" className="p-1.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200" data-testid="asistent-close">
              <X size={16} />
            </button>
          </div>

          {/* Quick actions (only when no messages) */}
          {messages.length === 0 && (
            <div className="p-4 space-y-2" data-testid="asistent-quick">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Što mogu učiniti za vas?</p>
              <QuickBtn icon={<Search size={14} />} label="Pretraži portal" onClick={() => quickAction('search')} />
              <QuickBtn icon={<Compass size={14} />} label="Odvedi me (navigacija)" onClick={() => quickAction('nav')} />
              {articleId ? (
                <QuickBtn icon={<BookOpen size={14} />} label="Sažmi ovaj članak" onClick={() => quickAction('summary')} testid="asistent-summary-btn" />
              ) : (
                <p className="text-[11px] text-gray-400 dark:text-gray-500 italic pt-1">Sažetak je dostupan na stranici članka.</p>
              )}
            </div>
          )}

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2" aria-live="polite" data-testid="asistent-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-xl text-sm leading-snug ${m.role === 'user' ? 'bg-red-600 text-white rounded-br-sm' : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-bl-sm'}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-300 px-3 py-2 rounded-xl text-sm flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin" /> Sažimam…
                </div>
              </div>
            )}

            {suggestions.articles?.length > 0 && !loading && (
              <div className="mt-2 space-y-1">
                <p className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500">Prijedlozi</p>
                {suggestions.articles.slice(0, 5).map(a => (
                  <button key={a.id}
                    onClick={() => { setState('minimized'); navigate(`/clanak/${a.slug}`); }}
                    className="w-full text-left px-2 py-1.5 rounded-lg text-xs text-gray-700 dark:text-gray-200 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-700 dark:hover:text-red-300 transition-colors"
                    data-testid={`asistent-suggestion-${a.id}`}>
                    {a.title_hr}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Input */}
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-gray-100 dark:border-gray-800 p-3 flex gap-2 bg-white dark:bg-[#0f172a]">
            <input
              ref={inputRef} type="text" value={input} onChange={e => setInput(e.target.value)}
              placeholder={articleId ? 'Pretraži, navigiraj ili sažmi…' : 'Pretraži ili navigiraj…'}
              className="flex-1 bg-gray-50 dark:bg-[#0b1220] border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-gray-100 outline-none focus:border-red-400 placeholder:text-gray-400 dark:placeholder:text-gray-500"
              data-testid="asistent-input"
              aria-label="Poruka asistentu"
            />
            <button type="submit" disabled={!input.trim() || loading}
              className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg disabled:opacity-40"
              data-testid="asistent-send" aria-label="Pošalji">
              <Send size={15} />
            </button>
          </form>
          <div className="px-3 pb-2 text-[10px] text-gray-400 dark:text-gray-500 flex items-center gap-1">
            <CornerDownLeft size={10} /> Enter za slanje · ESC za minimiziranje
          </div>
        </div>
      )}
    </>
  );
}

function QuickBtn({ icon, label, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="w-full flex items-center gap-2 px-3 py-2 text-left rounded-lg border border-gray-200 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-200 hover:border-red-400 hover:text-red-700 dark:hover:text-red-300 dark:hover:border-red-500 transition-colors">
      <span className="text-red-600">{icon}</span> {label}
    </button>
  );
}
